/**
 * useAdminStatus - Admin Status Detection and Management Hook
 *
 * Purpose:
 * Manages admin/owner mode detection, effective mode computation, and admin-related state.
 * Handles query param override precedence, backend admin detection, localStorage persistence,
 * and admin mode initialization. Extracted from SessionContext.jsx to reduce responsibility overload.
 *
 * High-level Workflow:
 * 1. On mount/URL change: Check query param override (?admin=1/0) or call backend auth endpoint
 * 2. Determine admin status using deterministic rules (query param → backend detection)
 * 3. Update isAdminUser state and debugInfo
 * 4. Initialize selectedOwnerId when admin mode is enabled
 * 5. Clear admin-specific localStorage when not admin
 * 6. Persist admin state to localStorage (only if from query param)
 * 7. Compute effectiveMode based on isAdminUser and viewAsOwner
 *
 * Assumptions & Invariants:
 * - Query param override (?admin=1/0) takes highest precedence for admin status
 * - Admin auth service is dynamically imported (not bundled in owner portal)
 * - Portal detection is hostname-based (admin.* or owner/clcvacation domains)
 * - localStorage persistence is conditional (admin state only persisted if from query param)
 * - Effective mode: "owner" (pure owner), "owner_view" (admin viewing as owner), "admin" (full admin)
 *
 * Dependencies:
 * Internal:
 *   - services/adminAuthService.js: getCurrentAdmin() for admin auth (dynamic import, not in owner bundle)
 *   - utils/portalDetection.js: detectPortal(), getApiBase()
 * External:
 *   - react: useState, useEffect, useCallback, useRef
 *
 * Version: 1.1.0
 *
 * Changelog:
 * - 2026-01-26: Phase 3 - Extracted from SessionContext.jsx (verbatim, no logic changes)
 * - 2026-01-26: Phase 4 - Added mount guards to prevent setState after unmount, log gating
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { detectPortal, getApiBase } from '../utils/portalDetection.js';

/**
 * useAdminStatus - Admin status detection and management
 * 
 * @param {string} pathname - Current pathname (for popstate listener)
 * @param {Object} [options] - Optional configuration
 * @param {Object|null} [options.initialAuthMe] - Bootstrap /me payload from owner-main (skips duplicate fetch)
 * @returns {Object} Admin status state and setters
 */
export function useAdminStatus(pathname, { initialAuthMe = null } = {}) {
  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';
  
  // Mounted ref to prevent setState after unmount
  const isMountedRef = useRef(true);

  // One-shot bootstrap from owner-main /me (avoids duplicate /me on calendar load)
  const bootstrapAuthMeRef = useRef(initialAuthMe);

  // Cleanup on unmount: mark as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper to check if we should set state (component still mounted)
  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  // Helper to check if debug logging is enabled
  const shouldLog = () => {
    if (typeof window === 'undefined') return false;
    return process.env.NODE_ENV === 'development' ||
           new URLSearchParams(window.location.search).get('debug') === '1';
  };
  
  // Helper functions for defaults (no mock fallback)
  const getDefaultOwnerId = useCallback(() => {
    // No default - will be set from API after authentication
    return null;
  }, []);

  // Check for query param override (highest precedence ALWAYS)
  const getQueryParamOverride = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    if (adminParam === '1') return { value: true, source: 'query' };
    if (adminParam === '0') return { value: false, source: 'query' };
    return null;
  }, []);

  /**
   * detectAdminFromBackend - Portal-aware admin status detection
   * 
   * Intent: Detects admin status by calling the appropriate auth endpoint based on detected portal.
   * Uses dynamic import for admin auth service to prevent bundling in owner portal. Handles both
   * admin and owner portals with different detection logic (admin: isSuperAdmin/capabilities,
   * owner: role-based check for superadmin/operator/admin roles).
   * 
   * Reliability (Phase 4):
   * - Mount guards prevent setState after unmount in calling code
   * - Note: Admin detection uses fetch() directly; AbortController could be added if needed
   * 
   * @returns {Promise<{isAdmin: boolean, debugInfo: object, currentUser: object|null}>} Admin status and debug information
   */
  // Auto-detect admin status from backend (non-blocking, never blocks render)
  // PORTAL-AWARE: Use correct endpoint based on detected portal
  const detectAdminFromBackend = useCallback(async () => {
    const portal = detectPortal();
    const apiBase = getApiBase();
    const endpoint = portal === 'admin' ? '/api/v1/admin/auth/me' : '/api/v1/owner/auth/me';
    
    let debugInfo = {
      status: 'loading',
      httpStatus: null,
      errorMessage: null,
      adminSource: 'backend',
      endpoint,
      portal,
    };

    try {
      // Portal-aware: use admin endpoint on admin domain, owner endpoint on owner domain
      let response;
      let data;
      
      if (portal === 'admin') {
        // Admin portal: use admin auth service (dynamic import to avoid bundling in owner portal)
        const { getCurrentAdmin } = await import('../services/adminAuthService.js');
        const adminUser = await getCurrentAdmin();
        if (adminUser) {
          // Admin user is authenticated
          const isAdmin = adminUser.isSuperAdmin || (adminUser.capabilities && adminUser.capabilities.length > 0);
          debugInfo.status = 'success';
          debugInfo.httpStatus = 200;
          debugInfo.isAdmin = isAdmin;
          return { isAdmin, debugInfo, currentUser: adminUser };
        } else {
          // Not authenticated
          debugInfo.status = 'unauthorized';
          debugInfo.httpStatus = 401;
          debugInfo.errorMessage = 'Not authenticated';
          return { isAdmin: false, debugInfo, currentUser: null };
        }
      } else {
        // Owner portal: reuse bootstrap /me from owner-main when available
        if (bootstrapAuthMeRef.current) {
          const data = bootstrapAuthMeRef.current;
          bootstrapAuthMeRef.current = null;
          const authUser = data?.user || data?.data?.user || data?.owner || data?.data?.owner || null;
          const userRoles = authUser?.roles || data?.roles || data?.data?.roles || [];
          const isAdmin = userRoles.some(role => ['superadmin', 'operator', 'admin'].includes(role));
          debugInfo.status = 'success';
          debugInfo.httpStatus = 200;
          debugInfo.isAdmin = isAdmin;
          return { isAdmin, debugInfo, currentUser: authUser };
        }

        // Owner portal: use owner auth endpoint (uses owner.sid cookie)
        response = await fetch('/api/v1/owner/auth/me', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });

        debugInfo.httpStatus = response.status;

        if (response.ok) {
          data = await response.json();
          // Check for admin status from user roles (shared RBAC)
          // Admin roles: superadmin, operator, admin
          const authUser = data.data?.user || data.user || data.data?.owner || data.owner || null;
          const userRoles = authUser?.roles || data.data?.roles || data.roles || [];
          const isAdmin = userRoles.some(role => ['superadmin', 'operator', 'admin'].includes(role));
          debugInfo.status = 'success';
          debugInfo.isAdmin = isAdmin;
          return { isAdmin, debugInfo, currentUser: authUser };
        } else {
          debugInfo.status = response.status === 401 || response.status === 403 ? 'unauthorized' : 'error';
          debugInfo.errorMessage = `HTTP ${response.status}`;
          return { isAdmin: false, debugInfo, currentUser: null };
        }
      }
    } catch (err) {
      debugInfo.status = 'error';
      debugInfo.errorMessage = err.message || 'Network error';
      return { isAdmin: false, debugInfo, currentUser: null };
    }
  }, []);

  /**
   * determineAdminStatus - Deterministic admin status resolution
   * 
   * Intent: Resolves admin status using deterministic rules with clear precedence. Query param
   * override (?admin=1/0) takes highest precedence, then falls back to backend detection.
   * This ensures predictable behavior and allows testing/debugging via query params.
   * 
   * Precedence:
   * 1. Query param override (?admin=1/0) - highest precedence
   * 2. Backend detection (detectAdminFromBackend) - primary path
   * 
   * @returns {Promise<{isAdmin: boolean, source: string, debugInfo: object}>} Admin status with source and debug info
   */
  // Determine admin enablement (deterministic rules)
  // CRITICAL: NO mock mode - always use backend detection
  const determineAdminStatus = useCallback(async () => {
    // Rule 1: Query override takes highest precedence ALWAYS
    const queryOverride = getQueryParamOverride();
    if (queryOverride) {
      return {
        isAdmin: queryOverride.value,
        source: 'query',
        debugInfo: { status: 'success', adminSource: 'query', httpStatus: null, errorMessage: null },
        currentUser: null,
      };
    }

    // Rule 2: Use backend detection (primary path - NO mock mode)
    const { isAdmin: detectedAdmin, debugInfo: backendDebugInfo, currentUser } = await detectAdminFromBackend();
    return {
      isAdmin: detectedAdmin,
      source: 'backend',
      debugInfo: backendDebugInfo,
      currentUser,
    };
  }, [getQueryParamOverride, detectAdminFromBackend]);

  // State
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [adminInsightsEnabled, setAdminInsightsEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('adminInsightsEnabled');
      if (stored !== null) return stored === 'true';
    }
    return false;
  });
  const [selectedOwnerId, setSelectedOwnerId] = useState(() => getDefaultOwnerId());
  const [viewAsOwner, setViewAsOwner] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('viewAsOwner');
      if (stored !== null) return stored === 'true';
    }
    return true; // Default: View as Owner ON
  });
  const [debugInfo, setDebugInfo] = useState({
    status: 'idle',
    httpStatus: null,
    errorMessage: null,
    adminSource: 'none',
    isAdmin: false,
    viewAsOwner: true,
  });
  const [currentUser, setCurrentUser] = useState(null);

  // Compute effective mode
  const effectiveMode = !isAdminUser ? 'owner' : (viewAsOwner ? 'owner_view' : 'admin');

  // When bootstrap /me arrives after login, apply without a second fetch
  useEffect(() => {
    if (!initialAuthMe) return;
    const authUser = initialAuthMe?.user || initialAuthMe?.data?.user || initialAuthMe?.owner || initialAuthMe?.data?.owner || null;
    const userRoles = authUser?.roles || initialAuthMe?.roles || initialAuthMe?.data?.roles || [];
    const isAdmin = userRoles.some(role => ['superadmin', 'operator', 'admin'].includes(role));
    safeSetState(setIsAdminUser, isAdmin);
    safeSetState(setCurrentUser, authUser);
    safeSetState(setDebugInfo, prev => ({
      ...prev,
      status: 'success',
      httpStatus: 200,
      isAdmin,
      adminSource: 'bootstrap',
    }));
  }, [initialAuthMe]);

  // Determine admin status on mount and URL changes
  useEffect(() => {
    const checkAdmin = async () => {
      const result = await determineAdminStatus();
      
      // Check if component is still mounted before setting state
      if (!isMountedRef.current) return;
      
      safeSetState(setIsAdminUser, result.isAdmin);
      safeSetState(setDebugInfo, prev => ({
        ...prev,
        ...result.debugInfo,
        isAdmin: result.isAdmin,
        adminSource: result.source,
      }));
      if (result.currentUser !== undefined) {
        safeSetState(setCurrentUser, result.currentUser);
      }

      // Initialize owner when admin mode is enabled (property selection handled by properties useEffect)
      if (result.isAdmin) {
        const currentOwner = selectedOwnerId || getDefaultOwnerId();
        if (!selectedOwnerId) {
          safeSetState(setSelectedOwnerId, currentOwner);
          if (result.source === 'query') {
            localStorage.setItem('selectedOwnerId', currentOwner);
          }
        }
        // Property selection (selectedListingId) is handled by the useEffect that runs after properties load
      } else {
        // Clear admin-specific localStorage when not admin
        if (typeof window !== 'undefined') {
          localStorage.removeItem('adminMode');
          localStorage.removeItem('adminInsightsEnabled');
          localStorage.removeItem('selectedOwnerId');
          localStorage.removeItem('selectedPropertyId'); // Legacy key cleanup
          localStorage.removeItem('viewAsOwner');
          // Note: owner_selected_listing_id is preserved for owner mode
        }
      }
    };

    checkAdmin();

    const handleLocationChange = () => {
      checkAdmin();
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [determineAdminStatus, getDefaultOwnerId, selectedOwnerId]);

  // When viewAsOwner changes, update debug info and disable admin insights
  useEffect(() => {
    safeSetState(setDebugInfo, prev => ({ ...prev, viewAsOwner }));
    if (viewAsOwner) {
      safeSetState(setAdminInsightsEnabled, false);
    }
    // Only persist if from query param
    const queryOverride = getQueryParamOverride();
    if (queryOverride && isAdminUser) {
      localStorage.setItem('viewAsOwner', String(viewAsOwner));
    }
  }, [viewAsOwner, getQueryParamOverride, isAdminUser]);

  // Persist admin state ONLY when from query param
  useEffect(() => {
    const queryOverride = getQueryParamOverride();
    if (queryOverride && isAdminUser === queryOverride.value) {
      localStorage.setItem('adminMode', String(isAdminUser));
      localStorage.setItem('adminInsightsEnabled', adminInsightsEnabled.toString());
      localStorage.setItem('selectedOwnerId', selectedOwnerId);
      // Note: selectedListingId is persisted via its own useEffect
      localStorage.setItem('viewAsOwner', viewAsOwner.toString());
    }
  }, [isAdminUser, adminInsightsEnabled, selectedOwnerId, viewAsOwner, getQueryParamOverride]);

  // Ensure owner is valid when admin mode is active (property selection handled by properties useEffect)
  useEffect(() => {
    if (isAdminUser && typeof window !== 'undefined') {
      if (!selectedOwnerId) {
        const defaultOwner = getDefaultOwnerId();
        safeSetState(setSelectedOwnerId, defaultOwner);
        const queryOverride = getQueryParamOverride();
        if (queryOverride) {
          localStorage.setItem('selectedOwnerId', defaultOwner);
        }
      }
      // Property selection (selectedListingId) is handled by the useEffect that runs after properties load
    }
  }, [isAdminUser, selectedOwnerId, getDefaultOwnerId, getQueryParamOverride]);

  return {
    isAdminUser,
    viewAsOwner,
    setViewAsOwner,
    effectiveMode,
    adminInsightsEnabled,
    setAdminInsightsEnabled,
    selectedOwnerId,
    setSelectedOwnerId,
    currentUser,
    debugInfo,
  };
}
